import { useState } from 'react';
import { Loader2, BarChart3, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function fmt(v) {
  if (v == null) return '--';
  const n = Number(v);
  return isNaN(n) ? v : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function VerdictBanner({ interpretation, deltaAbs, deltaPct }) {
  if (!interpretation) return null;
  const isPreserved = interpretation.startsWith('Ratio preserved');
  const isCantCompute = interpretation.startsWith('Cannot compute') || interpretation.startsWith('Portfolio mean is zero');

  let icon, bg;
  if (isCantCompute) {
    icon = <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
    bg = 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
  } else if (isPreserved) {
    icon = <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />;
    bg = 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800';
  } else {
    icon = <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />;
    bg = 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
  }

  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 ${bg}`}>
      {icon}
      <div className="space-y-1 text-sm">
        <p className="font-medium">{interpretation}</p>
        {deltaAbs != null && (
          <p className="text-muted-foreground">
            Delta: {fmt(deltaAbs)} GBP ({fmt(deltaPct)}%)
          </p>
        )}
      </div>
    </div>
  );
}

export function CancelledCohortRateCheckPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.cancelledCohortRateCheck();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const cohort = results?.cancelled_cohort;
  const portfolio = results?.portfolio_mean;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          Cancelled Cohort Rate Check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Compares the average nightly rate of cancelled bookings against the live
          portfolio mean. Smoobu revenue is re-fetched from the API (local copy was
          zeroed by the backfill). Validates whether the flat nightly-rate chart
          post-backfill is a ratio-preservation artefact.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <BarChart3 className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {results && (
          <div className="space-y-5">
            <VerdictBanner
              interpretation={results.interpretation}
              deltaAbs={results.delta?.absolute_gbp}
              deltaPct={results.delta?.percentage}
            />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat
                label="Cancelled bookings"
                value={cohort?.combined?.count ?? 0}
                sub={`Smoobu: ${cohort?.smoobu?.total_cancelled ?? 0} / Uplisting: ${cohort?.uplisting?.total_cancelled ?? 0}`}
              />
              <Stat
                label="Cohort nights"
                value={cohort?.combined?.nights ?? 0}
              />
              <Stat
                label="Cohort mean rate (GBP)"
                value={cohort?.combined?.mean_nightly_rate_gbp != null ? `\u00A3${fmt(cohort.combined.mean_nightly_rate_gbp)}` : '--'}
              />
              <Stat
                label="Portfolio mean rate (GBP)"
                value={portfolio?.mean_nightly_rate_gbp != null ? `\u00A3${fmt(portfolio.mean_nightly_rate_gbp)}` : '--'}
                sub={`${portfolio?.nights ?? 0} nights`}
              />
            </div>

            {/* Smoobu detail */}
            {cohort?.smoobu?.total_cancelled > 0 && (
              <div className="text-sm space-y-1">
                <p className="font-medium">Smoobu detail</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">API fetched:</span>{' '}
                    <span className="font-mono">{cohort.smoobu.api_fetched}</span>
                  </div>
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">Fetch failed:</span>{' '}
                    <span className="font-mono">{cohort.smoobu.api_fetch_failed}</span>
                  </div>
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">Zero from API:</span>{' '}
                    <span className="font-mono">{cohort.smoobu.zero_price_from_api}</span>
                  </div>
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">Revenue source:</span>{' '}
                    <span className="font-mono">{cohort.smoobu.revenue_source}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Uplisting detail */}
            {cohort?.uplisting?.total_cancelled > 0 && (
              <div className="text-sm space-y-1">
                <p className="font-medium">Uplisting detail</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">With local revenue:</span>{' '}
                    <span className="font-mono">{cohort.uplisting.with_local_revenue}</span>
                  </div>
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">Zero revenue:</span>{' '}
                    <span className="font-mono">{cohort.uplisting.zero_revenue}</span>
                  </div>
                  <div className="rounded border p-2">
                    <span className="text-muted-foreground">Nights used:</span>{' '}
                    <span className="font-mono">{cohort.uplisting.nights_with_revenue}</span>
                  </div>
                </div>
              </div>
            )}

            {/* FX info */}
            {results.fx_rates_used?.EUR_GBP && (
              <p className="text-xs text-muted-foreground">
                EUR/GBP rate used: {results.fx_rates_used.EUR_GBP}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
